// filepath: apps/web/src/design-system/Card.tsx
//
// FR-UI-CORE-9 / FR-UI-OPS-3 — Card primitive from the design system
// inventory (`docs/guides/design system/components/core/Card.jsx`).
//
// Ported from the reference JSX to TypeScript for the production
// React SPA. Token values are referenced via CSS custom properties
// so they resolve through the design-system stylesheet; no raw hex
// values are introduced here (per react-conventions.instructions.md
// "Styling" section).

import React from "react";

export type CardPadding = "sm" | "md" | "lg";

export interface CardProps {
  children?: React.ReactNode;
  /** Inner padding scale. Default: `md`. */
  padding?: CardPadding;
  /** Test hook forwarded onto the rendered element. */
  "data-testid"?: string;
  /** Section / grouping ARIA role (e.g. `region` for landmark). */
  role?: string;
  /** Accessible label when `role` is non-default. */
  "aria-label"?: string;
  /** Accessible labelled-by id when `role` is non-default. */
  "aria-labelledby"?: string;
  /** Extra inline style passthrough (token-only — no raw hex). */
  style?: React.CSSProperties;
  /** Extra className passthrough. */
  className?: string;
}

const PADDING_TOKEN: Record<CardPadding, string> = {
  sm: "var(--space-3)",
  md: "var(--space-4)",
  lg: "var(--space-6)",
};

/**
 * Card — neutral surface container with a token-driven border +
 * radius + padding. Pure presentation; no internal state.
 */
export function Card({
  children,
  padding = "md",
  style,
  className,
  ...rest
}: CardProps): React.ReactElement {
  return (
    <div
      {...rest}
      className={["ds-card", className].filter(Boolean).join(" ")}
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--border-default)",
        borderRadius: "var(--radius-md)",
        padding: PADDING_TOKEN[padding],
        ...style,
      }}
    >
      {children}
    </div>
  );
}
